import type { ReadTransaction, WriteTransaction } from 'replicache'

import {
  OveragesSubscription,
  Plan,
  Role,
  Team,
  TeamInvite,
  UsageRecord,
  teamBaseOps,
} from '@basehub/data/replicache/index'
import { hasAccessOrThrow, isUserIdOwner } from './auth'
import { invariant } from '@epic-web/invariant'
import { checkInviteValidity } from '../invite-util'
import { isServer } from '@basehub/shared-constants'
import { getBlockUsageFromTeam } from '@basehub/data/db/billing/getters'
import type { Executor } from '@basehub/data/db/queries'
import {
  calculatePeriodStartAtForNoSubscribedPlans,
  buildOveragesSubscription,
  buildBaseSubscription,
} from '@basehub/data/replicache/mutators/team-space/team/subscription-util'
import { Redis } from '@upstash/redis'
import { WithServerSecret } from '../../../tx/spec'
import { serverAuthorizedOrThrow } from '../../../tx/server-auth'
import type Stripe from 'stripe'
import { ensureBaseHubTransaction } from '@basehub/data/replicache/tx/iso'
import { getDeliveryApiRequestsUsage } from './api-usage'

async function getTeamBySlug(tx: ReadTransaction, slug: string) {
  const teams = await teamBaseOps.list(tx)

  return teams.find((team) => team.slug === slug)
}

const createTeam = async (
  tx: WriteTransaction,
  data: Team & { serverSecret: string }
) => {
  serverAuthorizedOrThrow(data.serverSecret)

  await teamBaseOps.put(tx, data)
  return data
}

async function addRepoToTeam(
  tx: WriteTransaction,
  { teamId, repoId }: WithServerSecret<{ teamId: string; repoId: string }>
) {
  ensureBaseHubTransaction(tx)
  const team = await teamBaseOps.get(tx, teamId)
  invariant(team, 'Team not found')

  hasAccessOrThrow({
    serverOnly: tx.serverOnly ? { ...tx.serverOnly, team } : undefined,
    clientOnly: tx.clientOnly,
    serverSecret: tx.serverSecret,
    allowedRoles: ['owner', 'member'],
    guestAccess: false,
  })

  await teamBaseOps.update(tx, {
    id: teamId,
    repoIds: [...team.repoIds, repoId],
  })
}

async function removeRepoFromTeam(
  tx: WriteTransaction,
  { teamId, repoId }: WithServerSecret<{ teamId: string; repoId: string }>
) {
  ensureBaseHubTransaction(tx)
  const team = await teamBaseOps.get(tx, teamId)
  invariant(team, 'Team not found')

  hasAccessOrThrow({
    serverOnly: tx.serverOnly ? { ...tx.serverOnly, team } : undefined,
    clientOnly: tx.clientOnly,
    serverSecret: tx.serverSecret,
    allowedRoles: ['owner', 'member'],
    guestAccess: false,
  })

  await teamBaseOps.update(tx, {
    id: teamId,
    repoIds: team.repoIds.filter((id) => id !== repoId),
  })
}

const updateTeamSettings = async (
  tx: WriteTransaction,
  {
    id,
    slug,
    title,
    description,
    avatarUrl,
  }: WithServerSecret<{
    id: string
    slug?: string
    title?: string
    description?: string
    avatarUrl?: string | null
  }>
) => {
  ensureBaseHubTransaction(tx)
  const team = await teamBaseOps.get(tx, id)
  invariant(team, 'Team not found')

  if (tx.serverOnly) {
    // ensure user is owner
    invariant(
      tx.serverOnly.session &&
        isUserIdOwner(team, tx.serverOnly.session.userId),
      'Unauthorized'
    )
  }

  await teamBaseOps.update(tx, {
    id,
    ...(slug && { slug }),
    ...(title && { title }),
    ...(description && { description }),
    ...(avatarUrl !== undefined && { avatarUrl }),
  })
}

const updateTeamUsageLimits = async (
  tx: WriteTransaction,
  {
    id,
    usageLimits,
  }: WithServerSecret<{
    id: string
    usageLimits: Partial<
      Record<keyof OveragesSubscription['usageRecords'], number | null>
    >
  }>
) => {
  ensureBaseHubTransaction(tx)
  const team = await teamBaseOps.get(tx, id)
  invariant(team, 'Team not found')
  invariant(team.plan_v2?.overagesSubscription, 'Team has no subscription')

  function executor() {
    ensureBaseHubTransaction(tx)
    invariant(isServer, 'executor() should only be called on the server')
    invariant(tx.serverOnly?.executor, 'executor not found')
    return tx.serverOnly.executor as Executor
  }

  function redis() {
    ensureBaseHubTransaction(tx)
    invariant(isServer, 'redis() should only be called on the server')
    invariant(tx.serverOnly?.redis, 'executor not found')
    return tx.serverOnly.redis as Redis
  }

  if (tx.serverOnly) {
    // ensure user is owner
    invariant(
      tx.serverOnly.session &&
        isUserIdOwner(team, tx.serverOnly.session.userId),
      'Unauthorized'
    )
  }

  if (team.plan_v2.type === 'personal') {
    invariant(
      team.plan_v2.overagesSubscription !== null,
      'Usage limits can only be updated if the account is subscribed.'
    )
  }

  if (tx.serverOnly) {
    // check if usageLimit for blocks is not lower than current usage
    if (usageLimits.blocks && usageLimits.blocks !== null) {
      const currentUsage = await getBlockUsageFromTeam(team, {
        db: executor() as any,
        redis: redis(),
      })
      invariant(
        usageLimits.blocks >= currentUsage.total,
        'Usage limit for blocks cannot be lower than current usage'
      )
    }

    // check if usageLimit for api requests is not lower than current usage
    if (usageLimits.apiRequests && usageLimits.apiRequests !== null) {
      const res = await getDeliveryApiRequestsUsage(
        tx.serverOnly.analyticsEngine,
        team.id,
        team.plan_v2?.overagesSubscription?.currentPeriodStartAt
          ? new Date(
              team.plan_v2.overagesSubscription?.currentPeriodStartAt
            ).getTime()
          : calculatePeriodStartAtForNoSubscribedPlans({
              teamCreatedAt: new Date(team.createdAt as string),
            }).getTime()
      )
      const currentUsage = res.request_count
      invariant(
        usageLimits.apiRequests >= currentUsage,
        'Usage limit for api requests cannot be lower than current usage'
      )
    }

    // check the other usages...(asset storage, asset requests)
  }

  const updatedUsageRecords: OveragesSubscription['usageRecords'] = {
    ...team.plan_v2.overagesSubscription?.usageRecords,
  }
  Object.entries(usageLimits).forEach(([key, value]) => {
    const key_ = key as keyof OveragesSubscription['usageRecords']

    if (updatedUsageRecords[key_]) {
      updatedUsageRecords[key_]!.limit = value
    } else {
      updatedUsageRecords[key_] = { limit: value, status: 'active' }
    }
  })

  await teamBaseOps.update(tx, {
    id,
    plan_v2: {
      ...team.plan_v2,
      overagesSubscription: {
        ...team.plan_v2.overagesSubscription,
        usageRecords: updatedUsageRecords,
      },
    },
  })
}

const dangerouslyUpdateTeam = async (
  tx: WriteTransaction,
  {
    serverSecret,
    newTeam,
  }: { newTeam: Partial<Team> & { id: string }; serverSecret: string }
) => {
  serverAuthorizedOrThrow(serverSecret)
  await teamBaseOps.update(tx, newTeam)
}

const updateTeamSubscription = async (
  tx: WriteTransaction,
  {
    id,
    subscriptionType,
    stripeSubscription,
    serverSecret,
    customerEmail,
  }: {
    id: Team['id']
    serverSecret: string
    subscriptionType: 'base' | 'overages'
    stripeSubscription: Stripe.Subscription
    customerEmail: string | null
    plan?: Plan
  }
) => {
  serverAuthorizedOrThrow(serverSecret)

  const team = await teamBaseOps.get(tx, id)
  invariant(team, 'Team not found')

  if (team.plan && !team.plan_v2) {
    if (team.plan === 'personal') {
      // subscribe to overages plan -> plan_v2
      invariant(
        subscriptionType === 'overages',
        `expected overages subscription subscribing legacy personal plan to overages`
      )
      await teamBaseOps.update(tx, {
        id,
        plan: null,
        plan_v2: {
          type: 'personal',
          overagesSubscription: buildOveragesSubscription({
            stripeSubscription,
            customerEmail,
          }),
        },
      })
    }
  }

  invariant(team.plan_v2, 'Team has no plan_v2')

  await teamBaseOps.update(tx, {
    id,
    plan_v2: {
      ...team.plan_v2,
      ...(subscriptionType === 'overages'
        ? {
            overagesSubscription: {
              ...team.plan_v2.overagesSubscription,
              ...buildOveragesSubscription({
                stripeSubscription,
                customerEmail,
              }),
            },
          }
        : {
            subscription: {
              ...team.plan_v2.overagesSubscription,
              ...buildBaseSubscription({ stripeSubscription, customerEmail }),
            },
          }),
    },
  })
}

const updateTeamAPIRequestsStatus = async (
  tx: WriteTransaction,
  {
    id,
    status,
    serverSecret,
  }: { id: Team['id']; status: UsageRecord['status']; serverSecret: string }
) => {
  serverAuthorizedOrThrow(serverSecret)

  const team = await teamBaseOps.get(tx, id)
  invariant(team, 'Team not found')
  invariant(team.plan_v2, 'Team has no plan_v2')
  if (!team.plan_v2.overagesSubscription) return

  await teamBaseOps.update(tx, {
    id,
    plan_v2: {
      ...team.plan_v2,
      overagesSubscription: {
        ...team.plan_v2.overagesSubscription,
        usageRecords: {
          ...team.plan_v2.overagesSubscription?.usageRecords,
          apiRequests: {
            ...team.plan_v2.overagesSubscription?.usageRecords.apiRequests,
            status,
          },
        },
      },
    },
  })
}

export const updateTeamRepositories = async (
  tx: WriteTransaction,
  { id, repoIds, serverSecret }: WithServerSecret<Pick<Team, 'id' | 'repoIds'>>
) => {
  serverAuthorizedOrThrow(serverSecret)

  const team = await teamBaseOps.get(tx, id)
  invariant(team, 'Team not found')

  await teamBaseOps.update(tx, { id, repoIds })
}

export const deleteTeam = async (
  tx: WriteTransaction,
  {
    id,
  }: WithServerSecret<{
    id: string
  }>
) => {
  ensureBaseHubTransaction(tx)
  const team = await teamBaseOps.get(tx, id)
  invariant(team, 'Team not found')

  if (tx.serverOnly) {
    // ensure user is owner
    invariant(
      tx.serverOnly.session &&
        isUserIdOwner(team, tx.serverOnly.session.userId),
      'Unauthorized'
    )
  }

  await teamBaseOps.delete(tx, id)
  return team
}

export const changeUserRole = async (
  tx: WriteTransaction,
  {
    teamId,
    userId,
    newRole,
    newRepoIds,
    hasAllAccess,
  }: WithServerSecret<{
    teamId: string
    userId: string
    newRole: Role
    newRepoIds?: string[] | null
    hasAllAccess: boolean
  }>
) => {
  ensureBaseHubTransaction(tx)
  const team = await teamBaseOps.get(tx, teamId)
  invariant(team, 'Team not found')

  if (tx.serverOnly) {
    // ensure user is owner
    invariant(
      tx.serverOnly.session &&
        isUserIdOwner(team, tx.serverOnly.session.userId),
      'Unauthorized'
    )
  }

  if (newRole === 'owner' && !hasAllAccess) {
    throw new Error('Owners must have all access')
  }

  await teamBaseOps.update(tx, {
    id: teamId,
    members: team.members.map((m) => {
      if (m.userId === userId) {
        return { ...m, role: newRole, repoIds: newRepoIds, hasAllAccess }
      }
      return m
    }),
  })
}

export const removeTeamInvitation = async (
  tx: WriteTransaction,
  {
    teamId,
    inviteToken,
  }: WithServerSecret<{ teamId: string; inviteToken: string }>
) => {
  ensureBaseHubTransaction(tx)
  const team = await teamBaseOps.get(tx, teamId)
  invariant(team, 'Team not found')

  if (tx.serverOnly) {
    // ensure user is owner
    invariant(
      tx.serverOnly.session &&
        isUserIdOwner(team, tx.serverOnly.session.userId),
      'Unauthorized'
    )
  }

  await teamBaseOps.update(tx, {
    id: teamId,
    invites: team.invites.filter((i) => i.token !== inviteToken),
  })
}

type InviteType<T> = T extends Array<infer U> ? U[] : T

const bulkInviteEmailsToTeam = async (
  tx: WriteTransaction,
  {
    teamId,
    invite,
    emails,
  }: WithServerSecret<{
    teamId: string
    invite: Omit<Team['invites'][number], 'emailLock'>
    emails: Team['invites'][number]['emailLock'][]
  }>
) => {
  ensureBaseHubTransaction(tx)
  const team = await teamBaseOps.get(tx, teamId)
  invariant(team, 'Team not found')

  if (tx.serverOnly) {
    // ensure user is owner
    invariant(
      tx.serverOnly.session &&
        isUserIdOwner(team, tx.serverOnly.session.userId),
      'Unauthorized'
    )
  }

  const invites = emails.map((email) => ({
    ...invite,
    emailLock: email,
  }))

  await teamBaseOps.update(tx, {
    id: teamId,
    invites: [...(team?.invites ?? []), ...invites],
  })

  return {
    emails,
    invites,
    ownerUsername: tx.serverOnly?.session?.sessionClaims.username ?? '',
    teamName: team.title,
  }
}

const inviteToTeam = async (
  tx: WriteTransaction,
  {
    teamId,
    invite,
  }: WithServerSecret<{
    teamId: string
    invite: InviteType<Team['invites'] | Team['invites'][number]>
  }>
) => {
  ensureBaseHubTransaction(tx)
  const team = await teamBaseOps.get(tx, teamId)
  invariant(team, 'Team not found')

  if (tx.serverOnly) {
    // ensure user is owner
    invariant(
      tx.serverOnly.session &&
        isUserIdOwner(team, tx.serverOnly.session.userId),
      'Unauthorized'
    )
  }

  await teamBaseOps.update(tx, {
    id: teamId,
    invites: Array.isArray(invite)
      ? [...team.invites, ...invite]
      : [...team.invites, invite],
  })

  return {
    invites: Array.isArray(invite) ? invite : [invite],
    ownerUsername: tx.serverOnly?.session?.sessionClaims.username ?? '',
    teamName: team.title,
  } as {
    invites: InviteType<Team['invites']>
    ownerUsername: string
    teamName: string
  }
}

// or leaveTeam
const removeFromTeam = async (
  tx: WriteTransaction,
  {
    teamId,
    userId,
  }: WithServerSecret<{
    teamId: string
    userId: string
  }>
) => {
  ensureBaseHubTransaction(tx)

  const team = await teamBaseOps.get(tx, teamId)
  invariant(team, 'Team not found')

  if (tx.serverOnly) {
    const isRemovingSelf = userId === tx.serverOnly.session?.userId
    const isOwner = isUserIdOwner(team, tx.serverOnly.session?.userId ?? '')

    // Prevent owners from removing themselves from the team
    // and prevent non-owners from removing others
    invariant(isRemovingSelf !== isOwner, 'Unauthorized')
  }

  const newMembers = team.members.filter((m) => m.userId !== userId)

  // Gets any owner left in the project or the oldest user with member role
  const memberWithMostSeniority = newMembers.reduce((acc, m) => {
    if (m.role === 'owner') {
      return m
    }
    if (m.role === 'member' && acc.role !== 'member') return m
    return new Date(m.createdAt) < new Date(acc.createdAt) ? m : acc
  })

  await teamBaseOps.update(tx, {
    id: teamId,
    members: newMembers.map((m) => {
      if (m.userId === memberWithMostSeniority.userId) {
        // if there are no owners left, make them the owner
        return { ...m, role: 'owner', hasAllAccess: true, repoIds: null }
      }
      return m
    }),
  })

  return {
    removedUserId: userId,
    ownerUsername: tx.serverOnly?.session?.sessionClaims.username ?? '',
    teamName: team.title,
  }
}

/**
 * Counter-intuitive mutator. This mutator can only be ran from a non-member of the team.
 * Receives an invite token and a userId.
 * If the invite token is valid, it will add the user to the team.
 */
const acceptInviteToTeam = async (
  tx: WriteTransaction,
  {
    teamId,
    inviteToken,
    isoNow,
    userId,
  }: WithServerSecret<{
    teamId: string
    inviteToken: string
    isoNow: string
    userId: string
  }>
) => {
  ensureBaseHubTransaction(tx)
  const team = await teamBaseOps.get(tx, teamId)
  invariant(team, 'Team not found')

  if (tx.serverOnly) {
    invariant(
      tx.serverOnly.session && userId === tx.serverOnly.session.userId,
      'Unauthorized'
    )
  }

  const invite = team.invites.find((i) => i.token === inviteToken)
  invariant(invite, 'Invite not found')

  if (tx.serverOnly) {
    const status = checkInviteValidity({
      session: tx.serverOnly.session ?? null,
      team,
      invite,
    })
    if (status !== 'valid') {
      throw new Error(`Invalid invite: ${status}`)
    }
  }

  // you're in!

  await teamBaseOps.update(tx, {
    id: teamId,
    members: [
      ...team.members,
      {
        createdAt: isoNow,
        role: invite.role,
        hasAllAccess: invite.hasAllAccess ?? true,
        repoIds: invite.repoIds ?? null,
        userId,
      },
    ],
    invites: team.invites.map((i) => {
      if (i.token === invite.token) {
        return { ...invite, useCount: invite.useCount + 1 }
      } else if (
        i.emailLock &&
        i.emailLock.toLowerCase() ===
          tx.serverOnly?.session?.sessionClaims.email.toLowerCase()
      ) {
        return { ...i, useCount: i.useCount + 1 }
      }
      return i
    }),
  })
}

export const resetTeamInvitation = async (
  tx: WriteTransaction,
  {
    teamId,
    oldInviteToken,
    newInvite,
  }: WithServerSecret<{
    teamId: string
    oldInviteToken: string
    newInvite: TeamInvite
  }>
) => {
  ensureBaseHubTransaction(tx)

  // ensure user is owner
  hasAccessOrThrow({
    serverOnly: tx.serverOnly,
    serverSecret: tx.serverSecret,
    clientOnly: tx.clientOnly,
    allowedRoles: ['owner'],
    guestAccess: false,
  })

  const team = await teamBaseOps.get(tx, teamId)
  invariant(team, 'Repo not found')

  const invite = team.invites.find((i) => i.token === oldInviteToken)
  invariant(!!invite, 'Invite not found')
  invariant(!invite.emailLock, 'Can not reset invite for email invite')
  invariant(!invite.userIdLock, 'Can not reset invite for user invite')

  await teamBaseOps.update(tx, {
    id: teamId,
    invites: (team.invites ?? []).filter((i) => i.token !== oldInviteToken),
  })

  await inviteToTeam(tx, {
    invite: newInvite,
    teamId: teamId,
  })
}

export const teamMutators = {
  getTeam: teamBaseOps.get,
  createTeam, // don't expose raw `putTeam`. only `createTeam`, that will limit to one team per space
  dangerouslyUpdateTeam,
  deleteTeam, // don't expose raw `deleteTeam` as this is a sensitive action that only `owners` can do.
  listTeams: teamBaseOps.list,
  updateTeamUsageLimits,
  updateTeamSettings,
  updateTeamSubscription,
  updateTeamRepositories,
  getTeamBySlug,
  addRepoToTeam,
  removeRepoFromTeam,
  inviteToTeam,
  bulkInviteEmailsToTeam,
  acceptInviteToTeam,
  removeFromTeam,
  changeUserRole,
  updateTeamAPIRequestsStatus,
  removeTeamInvitation,
  resetTeamInvitation,
}
